import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { confirmFileUpload } from '@editor2/services/apiService';
import {
	addLectureSlideDeck,
	incrementActiveFileUploads,
	decrementActiveFileUploads
} from '@editor2/reducers/editor/editorSlice';

import { useS3Upload } from './useS3Upload';
import { type CourseFile, type PendingFile, type PersistedFile } from './types';

interface UseHandleFilesSelectedOptions {
	courseId: number;
	setFiles: React.Dispatch<React.SetStateAction<CourseFile[]>>;
	expandedIdsRef: React.MutableRefObject<Set<string>>;
}

interface UseHandleFilesSelectedResult {
	handleFilesSelected: (files: File[]) => void;
}

export const useHandleFilesSelected = ({
	courseId,
	setFiles,
	expandedIdsRef
}: UseHandleFilesSelectedOptions): UseHandleFilesSelectedResult => {
	const dispatch = useDispatch();
	const { upload } = useS3Upload();

	const handleFilesSelected = useCallback(
		(files: File[]) => {
			for (const file of files) {
				const fileId = crypto.randomUUID();

				// Expand the detail row immediately so progress is visible.
				expandedIdsRef.current.add(fileId);

				setFiles((prev) => [
					...prev,
					{
						id: fileId,
						fileName: file.name,
						associatedFamilyId: '',
						isSelfServeSample: false,
						upload: { status: 'uploading' as const, progress: 0 }
					} satisfies PendingFile
				]);

				dispatch(incrementActiveFileUploads({ courseId }));

				/**
				 * The IIFE is used here to run an async function in this callback without
				 * making the callback function itself async. This is done because React
				 * does not expect or handle async functions as event handlers well.
				 */
				void (async () => {
					try {
						const key = await upload(file, (pct) => {
							setFiles((prev) =>
								prev.map((f) => {
									if (f.id !== fileId || !f.upload) return f;
									return { ...f, upload: { ...f.upload, progress: pct } };
								})
							);
						});

						const { data } = await confirmFileUpload(key);

						const serverFile = data.file;

						const lectureSlideDeckFamilyId = crypto.randomUUID();

						dispatch(
							addLectureSlideDeck({
								courseId,
								deck: {
									family_id: lectureSlideDeckFamilyId,
									type: 'NG::Soomo::LectureSlideDeck',
									associated_family_ids: [],
									self_serve_sample: false,
									file: serverFile
								}
							})
						);

						// Drop the upload state so the row becomes a persisted-shape file:
						// the deck is now in Redux, and a lingering `upload` field would keep
						// the row drag-disabled until the next save + refresh (see TECH-2543).
						setFiles((prev) =>
							prev.map((f): CourseFile => {
								if (f.id !== fileId) return f;
								return {
									id: f.id,
									fileName: serverFile.original_name,
									associatedFamilyId: f.associatedFamilyId,
									isSelfServeSample: f.isSelfServeSample,
									lectureSlideDeckFamilyId,
									filesizeBytes: serverFile.filesize_bytes,
									uploadedAt: serverFile.uploaded_at,
									uploaderName: serverFile.uploader_name,
									notes: f.notes
								} satisfies PersistedFile;
							})
						);
					} catch (err) {
						const message = err instanceof Error ? err.message : 'Upload failed';
						console.error(`[Files] Upload failed for "${file.name}":`, err);
						setFiles((prev) =>
							prev.map((f) => {
								if (f.id !== fileId || !f.upload) return f;
								return { ...f, upload: { ...f.upload, status: 'failed' as const, error: message } };
							})
						);
					} finally {
						dispatch(decrementActiveFileUploads({ courseId }));
					}
				})();
			}
		},
		[courseId, dispatch, expandedIdsRef, setFiles, upload]
	);

	return { handleFilesSelected };
};
